import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';

import { FREE_MONTH_PROMO } from 'constants/freeMonthPromo';
import { Button } from '../ui/button';

/**
 * Marketing dialog advertising the SIMPLIFIED free-month promo code.
 * Display-only; user enters the code on Stripe Checkout.
 */
export function FreeMonthPromoModal({ isOpen, onClose, onCta }) {
	const [copied, setCopied] = useState(false);

	if (!isOpen) return null;

	const {
		PROMO_CODE,
		TITLE,
		BODY,
		CHECKOUT_HINT,
		CTA_LABEL,
		COPY_LABEL,
		COPIED_LABEL,
		PRICING_PATH,
	} = FREE_MONTH_PROMO;

	const handleCopy = async () => {
		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(PROMO_CODE);
			} else {
				const input = document.createElement('textarea');
				input.value = PROMO_CODE;
				document.body.appendChild(input);
				input.select();
				document.execCommand('copy');
				document.body.removeChild(input);
			}
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (error) {
			console.error('Error copying free month promo code:', error);
		}
	};

	const handleCta = () => {
		if (onCta) {
			onCta();
		} else if (onClose) {
			onClose();
		}
	};

	return (
		<div
			className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/70 px-4 backdrop-blur-sm"
			onClick={(e) => {
				if (e.target === e.currentTarget && onClose) {
					onClose();
				}
			}}
		>
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby="free-month-promo-title"
				className="relative w-full max-w-md rounded-2xl bg-card p-7 text-card-foreground shadow-lift"
			>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close modal"
					className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground"
				>
					<X className="h-4 w-4" />
				</button>

				<h2
					id="free-month-promo-title"
					className="pr-8 text-2xl font-bold"
				>
					{TITLE}
				</h2>

				<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
					{BODY}
				</p>

				<div className="mt-5 flex items-stretch gap-2">
					<input
						type="text"
						readOnly
						value={PROMO_CODE}
						aria-label="Promo code"
						className="h-11 flex-1 rounded-md border border-border bg-secondary/50 px-3 text-center text-base font-bold tracking-widest"
					/>
					<Button
						type="button"
						variant="outline"
						size="lg"
						onClick={handleCopy}
					>
						{copied ? COPIED_LABEL : COPY_LABEL}
					</Button>
				</div>

				<p className="mt-2 text-xs text-muted-foreground">{CHECKOUT_HINT}</p>

				<Button
					variant="signal"
					size="lg"
					className="mt-6 w-full"
					asChild
				>
					<Link to={PRICING_PATH} onClick={handleCta}>
						{CTA_LABEL} <ArrowRight />
					</Link>
				</Button>
			</div>
		</div>
	);
}
